import { memo, useCallback, useMemo } from 'react';
import { useLoaderData, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik';
import * as yup from 'yup';

import { date, dialog, dom } from 'app/utils';
import { Breadcrumb } from 'app/components';
import { Button, Label } from 'app/components/common';
import { Form } from 'app/components/layout';
import { Field, Select, TextArea } from 'app/components/form';
import { templates } from 'app/components/utils';
import { offers } from 'app/services';
import { IOffer } from 'app/models';

export default memo(() => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const controller = useFormik<IOffer>({
    initialValues: (useLoaderData() ?? {}) as IOffer,
    validationSchema: useMemo(() => yup.object({
      description: yup.string().required(),
      status: yup.number().oneOf([0, 1, 2]).required(),
    }), []),

    validateOnBlur: true,
    validateOnChange: false,
    validateOnMount: true,

    onSubmit: () => { },
  }), { handleSubmit, handleReset } = controller;

  const onSave = useCallback(() => {
    if (!controller.isValid) { dialog.toast({ message: t('app.validation.required'), severity: 'error' }); return; }

    offers.save(controller.values).then(({ success }) => {
      if (!success) return;

      dialog.toast({ severity: 'success', message: t('offers.save.success') });
      navigate('..');
    });
  }, [controller, t, navigate]);

  const { touched, errors } = controller;
  const getProps = useCallback((field: keyof IOffer): any => ({ ...controller.getFieldProps(field),
    invalid: !!(touched[field] && errors[field]),
  }), [controller, touched, errors]);

  const form: any = {
    className: dom.className('p-4', 'block', 'flex-auto', 'overflow-auto'),

    onSubmit: handleSubmit,
    onReset: handleReset,
  };

  return (
    <div className="h-full flex flex-column align-items-stretch">
      <div className="flex-none flex surface-200">
        <Breadcrumb className="flex-auto border-none border-noround bg-transparent" />
        <div className="flex flex-none m-2">
          <Button text severity="warning" href=".." icon="pi-times" label={t('app.cancel')} className="mr-2 px-3 py-1" />
          <Button icon="mdi-check" label={t('app.save')} className="px-3 py-1" onClick={onSave} />
        </div>
      </div>
      <form { ...form }>
        <Form responsive={true}>
          <Field text={t('offers.fields.sender')} error={controller.errors.sender} align="end" asterisk>
            { templates.profile(controller.values.sender) }
          </Field>
          <Field text={t('offers.fields.receiver')} error={controller.errors.receiver} align="end" asterisk className="pt-4">
            { templates.profile(controller.values.receiver) }
          </Field>
          <Field text={t('offers.fields.timestamp')} error={controller.errors.timestamp} align="end" asterisk className="pt-4">
            <Label text={date.format(controller.values.timestamp) ?? ''} icon="mdi-calendar-clock" className="px-3 py-2" />
          </Field>
          <Field text={t('offers.fields.responded')} error={controller.errors.responded} align="end" className="pt-4">
            <Label text={date.format(controller.values.responded) ?? ''} icon="mdi-calendar-clock" className="px-3 py-2" />
          </Field>
          <Field text={t('offers.fields.description')} error={controller.errors.description} align="end" asterisk className="pt-4">
            <TextArea { ...getProps('description') } style={{ height: '6rem' }} readOnly />
          </Field>
          <Field text={t('offers.fields.status')} error={controller.errors.status} align="end" asterisk className="pt-4">
            <Select { ...getProps('status') } options={[
              { label: t('app.status.rejected'), value: 0 },
              { label: t('app.status.waiting'), value: 1 },
              { label: t('app.status.approved'), value: 2 },
            ]} />
          </Field>
        </Form>
      </form>
    </div>
  );
});
